import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, of } from 'rxjs'
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators'

import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { DateService } from '@lemon/core/service/date.service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import {
  AsiakasImportDocument,
  AsiakasImportHistoriaDocument,
  AsiakasImportLahdejarjestelma,
  AsiakasImportParsePyynto,
  AsiakasImportParseVastaus,
  AsiakasImportRivi,
  AsiakasImportTallennaPyynto,
  AsiakasImportTallennaVastaus,
  AsiakasImportVirhekoodi,
} from 'app/_jaettu-lemonator/model/asiakas-import'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import {
  TuontiLahdejarjestelmaVaihtoehto,
  TuontiLahdejarjestelmaValintaDialog,
  TuontiLahdejarjestelmaValintaDialogData,
  TuontiLahdejarjestelmaValintaDialogResult,
} from './tuonti-lahdejarjestelma-valinta.dialog'

type AsiakasImportVaihe = 'idle' | 'processing' | 'preview' | 'done' | 'error'

interface TiedostonTiedot {
  /** null, kunnes backend on tunnistanut lähdejärjestelmän tai käyttäjä valinnut sen */
  lahdejarjestelma: AsiakasImportLahdejarjestelma | null
  arkistoTiedostonAvain: string
  tiedostonNimi: string
}

const LAHDEJARJESTELMA_VAIHTOEHDOT: TuontiLahdejarjestelmaVaihtoehto[] = [
  { arvo: 'netvisor', nimi: 'Netvisor (asiakasrekisteri-XLSX)' },
  { arvo: 'procountor', nimi: 'Procountor (liikekumppaniraportti-XLS)' },
]

const KASIN_VALITTAVAT_VIRHEKOODIT = new Set<AsiakasImportVirhekoodi>([
  AsiakasImportVirhekoodi.LAHDEJARJESTELMA_EPAVARMA,
  AsiakasImportVirhekoodi.OTSIKKORIVI_PUUTTUU,
  AsiakasImportVirhekoodi.SARAKKEET_PUUTTUVAT,
  AsiakasImportVirhekoodi.PARSINTAVIRHE,
  AsiakasImportVirhekoodi.EI_ASIAKKAITA,
])

@Component({
  selector: 'app-asiakas-import',
  templateUrl: './asiakas-import.component.html',
  styleUrls: ['./asiakas-import.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsiakasImportComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _kirjanpitoUriService = new KirjanpitoUriService()

  vaihe: AsiakasImportVaihe = 'idle'
  virhe: string | null = null
  naytaLahdejarjestelmanValinta = false

  asiakas: Asiakas | null = null
  asiakasObservable: Observable<Asiakas | null>

  private _tiedostonTiedotSubject = new BehaviorSubject<TiedostonTiedot | null>(null)
  tiedostonTiedotObservable: Observable<TiedostonTiedot | null> = this._tiedostonTiedotSubject.asObservable()

  private _rivitSubject = new BehaviorSubject<AsiakasImportRivi[]>([])
  private _lahdejarjestelmaDialogRef: MatDialogRef<TuontiLahdejarjestelmaValintaDialog, TuontiLahdejarjestelmaValintaDialogResult> | null = null
  private _peruttuArkistoTiedostonAvain: string | null = null
  rivitObservable: Observable<AsiakasImportRivi[]> = this._rivitSubject.asObservable()

  tallennusTulos: { tallennetut: number; ohitetutDuplikaatit: number } | null = null

  historiaObservable: Observable<AsiakasImportHistoriaDocument[]>

  aktiivinenTabiIndex = 0

  // Debug — näytetään vain devaajille
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  asiakasImportDocUriObservable: Observable<string>
  asiakasImportDocLinkkiObservable: Observable<string>
  asiakkaatCollectionUriObservable: Observable<string>
  asiakkaatCollectionLinkkiObservable: Observable<string>

  kayttajienNimetMapObservable: Observable<Map<string, string>>

  constructor(
    private _asiakasService: AsiakasService,
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _route: ActivatedRoute,
    private _router: Router,
    private _dateService: DateService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _laskuUriService: LaskuUriService,
    private _dialog: MatDialog,
  ) {
    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable

    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

    this.asiakasImportDocUriObservable = this.asiakasObservable.pipe(map(a => (a ? this._kirjanpitoUriService.annaAsiakasImportDocumentUri(a.avain) : '')))
    this.asiakasImportDocLinkkiObservable = this.asiakasImportDocUriObservable.pipe(map(uri => (uri ? this._debugService.createFirestoreLink(uri) : '')))
    // Laskutusasiakkaat elävät Lemonaid-firestoressa
    this.asiakkaatCollectionUriObservable = this.asiakasObservable.pipe(map(a => (a ? this._laskuUriService.getLaskuAsiakkaatCollectionUri(a.avain) : '')))
    this.asiakkaatCollectionLinkkiObservable = this.asiakkaatCollectionUriObservable.pipe(map(uri => (uri ? this._debugService.createFirestoreLinkLemonaid(uri) : '')))

    this.historiaObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of<AsiakasImportHistoriaDocument[]>([])
        }
        const uri = this._kirjanpitoUriService.annaAsiakasImportHistoriaCollectionUri(asiakas.avain)
        return this._firebase.firestoreCollection<AsiakasImportHistoriaDocument>(uri).listen()
      }),
      map(docs =>
        (docs || []).sort((a, b) => {
          const aTime = (a.tuotu as { seconds?: number })?.seconds ?? 0
          const bTime = (b.tuotu as { seconds?: number })?.seconds ?? 0
          return bTime - aTime
        }),
      ),
    )

    this.kayttajienNimetMapObservable = this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(
      map(kipit => {
        const m = new Map<string, string>()
        for (const kipi of kipit) {
          m.set(kipi.avain, kipi.etunimi + ' ' + kipi.sukunimi)
          if (kipi.lemonaidUid) {
            m.set(kipi.lemonaidUid, kipi.etunimi + ' ' + kipi.sukunimi)
          }
        }
        return m
      }),
    )
  }

  annaKayttajanNimi(uid: string | null | undefined, nimet: Map<string, string> | null): string {
    if (!uid) {
      return '-'
    }
    const nimi = nimet?.get(uid)
    return nimi ? `${nimi} (${uid})` : uid
  }

  ngOnInit() {
    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas = asiakas
    })

    const edellinenImportObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of<AsiakasImportDocument | null>(null)
        }
        const uri = this._kirjanpitoUriService.annaAsiakasImportDocumentUri(asiakas.avain)
        return this._firebase.firestoreDoc<AsiakasImportDocument>(uri).listen()
      }),
    )

    combineLatest([this._route.queryParams, edellinenImportObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([params, edellinenImport]) => {
        const lahdejarjestelma = (params['lahdejarjestelma'] as AsiakasImportLahdejarjestelma) || null
        const edellisenAvain = edellinenImport?.arkistoTiedostonAvain !== this._peruttuArkistoTiedostonAvain ? edellinenImport?.arkistoTiedostonAvain : null
        const arkistoTiedostonAvain = params['arkistoTiedostonAvain'] || edellisenAvain
        const tiedostonNimi = params['tiedostonNimi'] || edellinenImport?.tiedostonNimi || ''

        if (!arkistoTiedostonAvain) {
          this.vaihe = 'idle'
          this._changeDetectorRef.markForCheck()
          return
        }

        if (this._tiedostonTiedotSubject.value?.arkistoTiedostonAvain !== arkistoTiedostonAvain) {
          this._lahdejarjestelmaDialogRef?.close()
          this._tiedostonTiedotSubject.next({ lahdejarjestelma, arkistoTiedostonAvain, tiedostonNimi })
          this._parsiJaKysyLahdejarjestelmaTarvittaessa()
        }
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  siirryHallintokansioon() {
    if (!this.asiakas?.avain) {
      return
    }
    const d = this._dateService.currentLocalDate()
    this._router.navigate(['asiakkaat', this.asiakas.avain, 'kirjanpito', d.year, d.month, 'hallinto'])
  }

  peruuta() {
    this._peruttuArkistoTiedostonAvain = this._tiedostonTiedotSubject.value?.arkistoTiedostonAvain ?? this._peruttuArkistoTiedostonAvain
    this._lahdejarjestelmaDialogRef?.close()
    this.vaihe = 'idle'
    this.virhe = null
    this.naytaLahdejarjestelmanValinta = false
    this._rivitSubject.next([])
    this._tiedostonTiedotSubject.next(null)
    this.tallennusTulos = null
    this._router.navigate([], { relativeTo: this._route, queryParams: {} })
  }

  async tallenna() {
    const tiedot = this._tiedostonTiedotSubject.value
    const rivit = this._rivitSubject.value
    if (!this.asiakas || !tiedot?.lahdejarjestelma || !rivit.length) {
      return
    }
    this.vaihe = 'processing'
    this._ladataanService.aloitaLataaminen()
    this._changeDetectorRef.markForCheck()
    try {
      const pyynto: AsiakasImportTallennaPyynto = {
        asiakasAvain: this.asiakas.avain,
        rivit,
        arkistoTiedostonAvain: tiedot.arkistoTiedostonAvain,
        lahdejarjestelma: tiedot.lahdejarjestelma,
      }
      const vastaus = await this._firebase.functionsCall<AsiakasImportTallennaPyynto, AsiakasImportTallennaVastaus>('asiakasImportTallenna', pyynto)
      if (!vastaus.onnistui) {
        this.virhe = this._kaannaVirhekoodi(vastaus.virhekoodi)
        this.vaihe = 'error'
        return
      }
      this.tallennusTulos = {
        tallennetut: vastaus.tallennetut ?? 0,
        ohitetutDuplikaatit: vastaus.ohitetutDuplikaatit ?? 0,
      }
      this.vaihe = 'done'
      const viesti =
        `Tallennettiin ${this.tallennusTulos.tallennetut} asiakasta` + (this.tallennusTulos.ohitetutDuplikaatit ? `, ohitettiin ${this.tallennusTulos.ohitetutDuplikaatit} duplikaattia` : '')
      this._snackbar.open(viesti, 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Asiakkaiden tallennus epäonnistui.'
      this.vaihe = 'error'
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._changeDetectorRef.markForCheck()
    }
  }

  /** Avaa lähdejärjestelmädialogin ja parsii tiedoston valitulla järjestelmällä. Käytetään, kun tunnistus on epävarma tai parsinta epäonnistui. */
  async valitseLahdejarjestelmaJaParsi(viesti?: string) {
    const tiedot = this._tiedostonTiedotSubject.value
    if (!tiedot) {
      return
    }
    const data: TuontiLahdejarjestelmaValintaDialogData = {
      otsikko: 'Valitse lähdejärjestelmä',
      tiedostonNimi: tiedot.tiedostonNimi || tiedot.arkistoTiedostonAvain,
      viesti,
      vaihtoehdot: LAHDEJARJESTELMA_VAIHTOEHDOT,
    }
    this._lahdejarjestelmaDialogRef?.close()
    const dialogRef = this._dialog.open<TuontiLahdejarjestelmaValintaDialog, TuontiLahdejarjestelmaValintaDialogData, TuontiLahdejarjestelmaValintaDialogResult>(TuontiLahdejarjestelmaValintaDialog, { data })
    this._lahdejarjestelmaDialogRef = dialogRef
    const result = await firstValueFrom(dialogRef.afterClosed())
    if (this._lahdejarjestelmaDialogRef === dialogRef) {
      this._lahdejarjestelmaDialogRef = null
    }
    if (result?.lahdejarjestelma && this._tiedostonTiedotSubject.value?.arkistoTiedostonAvain === tiedot.arkistoTiedostonAvain) {
      await this._kaynnistaParsinta(result.lahdejarjestelma)
    }
  }

  private async _parsiJaKysyLahdejarjestelmaTarvittaessa() {
    const virhekoodi = await this._kaynnistaParsinta()
    if (virhekoodi === AsiakasImportVirhekoodi.LAHDEJARJESTELMA_EPAVARMA) {
      await this.valitseLahdejarjestelmaJaParsi('Lähdejärjestelmää ei voitu tunnistaa tiedostosta automaattisesti.')
    }
  }

  private async _kaynnistaParsinta(lahdejarjestelma: AsiakasImportLahdejarjestelma | null = this._tiedostonTiedotSubject.value?.lahdejarjestelma ?? null) {
    this.virhe = null
    this.naytaLahdejarjestelmanValinta = false
    this.vaihe = 'processing'
    this._ladataanService.aloitaLataaminen()
    this._changeDetectorRef.markForCheck()

    try {
      const asiakas = await firstValueFrom(
        this._asiakasService.nykyinenAsiakasObservable.pipe(
          filter(a => !!a),
          take(1),
        ),
      )
      const tiedot = this._tiedostonTiedotSubject.value
      if (!asiakas || !tiedot) {
        return null
      }

      const pyynto: AsiakasImportParsePyynto = {
        asiakasAvain: asiakas.avain,
        arkistoTiedostonAvain: tiedot.arkistoTiedostonAvain,
      }
      if (lahdejarjestelma) {
        pyynto.lahdejarjestelma = lahdejarjestelma
      }
      const vastaus = await this._firebase.functionsCall<AsiakasImportParsePyynto, AsiakasImportParseVastaus>('asiakasImportParse', pyynto)
      // Käyttäjä on voinut perua tai valita toisen tiedoston parsinnan aikana, jolloin vanha vastaus ei saa avata dialogia eikä korvata näkymää
      if (this._tiedostonTiedotSubject.value?.arkistoTiedostonAvain !== tiedot.arkistoTiedostonAvain) {
        return null
      }

      if (!vastaus.onnistui || !vastaus.rivit) {
        this.virhe = this._kaannaVirhekoodi(vastaus.virhekoodi, vastaus.virheviesti)
        this.naytaLahdejarjestelmanValinta = !!vastaus.virhekoodi && KASIN_VALITTAVAT_VIRHEKOODIT.has(vastaus.virhekoodi)
        this.vaihe = 'error'
        return vastaus.virhekoodi ?? AsiakasImportVirhekoodi.TUNTEMATON_VIRHE
      }
      this._tiedostonTiedotSubject.next({ ...tiedot, lahdejarjestelma: vastaus.lahdejarjestelma ?? lahdejarjestelma })
      this._rivitSubject.next(vastaus.rivit)
      this.vaihe = 'preview'
      return null
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Tiedoston parsinta epäonnistui.'
      this.vaihe = 'error'
      return AsiakasImportVirhekoodi.TUNTEMATON_VIRHE
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._changeDetectorRef.markForCheck()
    }
  }

  private _kaannaVirhekoodi(koodi: AsiakasImportVirhekoodi | undefined, lisatieto?: string): string {
    switch (koodi) {
      case AsiakasImportVirhekoodi.LAHDEJARJESTELMA_EPAVARMA:
        return 'Lähdejärjestelmää ei voitu tunnistaa tiedostosta automaattisesti. Valitse lähdejärjestelmä.'
      case AsiakasImportVirhekoodi.METADATA_EI_LOYDY:
        return 'Tiedoston metadataa ei löytynyt. Tarkista, että tiedosto on edelleen arkistossa.'
      case AsiakasImportVirhekoodi.TIEDOSTOA_EI_LOYDY:
        return 'Tiedostoa ei löytynyt tallennuspaikasta. Se on voitu poistaa tai siirtää.'
      case AsiakasImportVirhekoodi.OTSIKKORIVI_PUUTTUU:
        return 'Tiedostosta ei löytynyt otsikkoriviä. Tarkista, että tiedosto on oikeassa muodossa.'
      case AsiakasImportVirhekoodi.SARAKKEET_PUUTTUVAT:
        return 'Tiedostosta puuttuu pakollinen sarake (vähintään Nimi).'
      case AsiakasImportVirhekoodi.EI_ASIAKKAITA:
        return 'Tiedostosta ei löytynyt yhtään asiakasta.'
      case AsiakasImportVirhekoodi.PARSINTAVIRHE:
        return lisatieto ? `Parsintavirhe: ${lisatieto}` : 'Parsintavirhe tiedoston lukemisessa.'
      case AsiakasImportVirhekoodi.TUNTEMATON_VIRHE:
      default:
        return 'Toiminto epäonnistui tuntemattoman virheen vuoksi. Yritä uudelleen.'
    }
  }
}
